import React, { useState, useRef, useEffect } from 'react';
import { useUserDetialsContext } from '../context/user-details';
import PageContainer from '../components/UI/PageContainer';
import { getSubmissionsDataApi } from '../api/submissions-database';

// CRA/Webpack uses process.env — same as LabV3.jsx (not Vite's import.meta.env).
const API_BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/').replace(
    /\/+$/,
    ''
);

const STEP = { UPLOAD: 'upload', PROCESSING: 'processing', CHAT: 'chat' };
const ROUTE_LABELS = {
    rag: 'RAG + synopsis',
    full_script: 'Full script',
};

const formatSubmissionDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
};

/** Ring spinner — avoids emoji spinners that look janky in browsers */
function Spinner({ size = 'md', className = '' }) {
    const sizes = {
        sm: 'h-5 w-5 border-2',
        md: 'h-10 w-10 border-2',
        lg: 'h-14 w-14 border-[3px]',
    };
    return (
        <div
            className={`rounded-full border-gray-700 border-t-purple-500 border-r-purple-500/30 animate-spin ${sizes[size]} ${className}`}
            role="status"
            aria-label="Loading"
        />
    );
}

const VectorInspector = ({ jobId, authHeader }) => {
    const [open, setOpen] = useState(false);
    const [chunks, setChunks] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedIdx, setExpandedIdx] = useState(null);

    const load = async () => {
        if (chunks) { setOpen(true); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/rag/v1/chunks/${jobId}/?limit=30`, {
                headers: authHeader,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to load chunks');
            setChunks(data);
            setOpen(true);
        } catch (e) {
            setError(String(e));
        } finally {
            setLoading(false);
        }
    };

    const toggle = () => {
        if (!open && !chunks) { load(); return; }
        setOpen(o => !o);
    };

    return (
        <div className="mt-4 border border-gray-700 rounded-xl overflow-hidden">
            <button
                onClick={toggle}
                className="w-full flex items-center justify-between px-5 py-3 bg-gray-900 hover:bg-gray-800 transition-colors text-left"
            >
                <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-purple-400">⬡</span>
                    <span className="text-sm font-medium text-gray-300">Vector Index Inspector</span>
                    {chunks && (
                        <span className="text-xs text-gray-500 ml-1">
                            — {chunks.total_chunks} chunks total, showing {chunks.sample_size}
                        </span>
                    )}
                </div>
                <span className="text-gray-500 text-xs">{open ? '▾ collapse' : '▸ expand'}</span>
            </button>

            {loading && (
                <div className="bg-gray-950 px-5 py-5 flex items-center gap-4">
                    <Spinner size="sm" />
                    <span className="text-sm text-gray-400">Loading chunks from FAISS index…</span>
                </div>
            )}

            {error && (
                <div className="bg-gray-950 px-5 py-3 text-sm text-red-400">{error}</div>
            )}

            {open && chunks && (
                <div className="bg-gray-950 px-5 py-4">
                    <p className="text-xs text-gray-600 mb-4">
                        Each row below is one chunk stored in the FAISS vector index.
                        The embedding model converted each chunk's text into a ~1,536-dimension float vector.
                        At query time, your question is embedded the same way and the closest vectors are retrieved.
                    </p>

                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                        {chunks.chunks.map((chunk, i) => (
                            <div
                                key={i}
                                className="border border-gray-800 rounded-lg overflow-hidden"
                            >
                                {/* Chunk header row */}
                                <button
                                    className="w-full flex items-center gap-3 px-4 py-2 bg-gray-900/60 hover:bg-gray-800/60 transition-colors text-left"
                                    onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                                >
                                    <span className="text-xs font-mono text-purple-400 w-14 flex-shrink-0">
                                        #{chunk.chunk_index ?? i}
                                    </span>
                                    <span className="text-xs text-gray-500 w-24 flex-shrink-0">
                                        {chunk.char_count.toLocaleString()} chars
                                    </span>
                                    <span className="text-xs text-gray-500 w-24 flex-shrink-0">
                                        ~{chunk.estimated_tokens.toLocaleString()} tokens
                                    </span>
                                    <span className="text-xs text-gray-400 flex-1 truncate font-mono">
                                        {chunk.text.slice(0, 100)}…
                                    </span>
                                    <span className="text-gray-600 text-xs flex-shrink-0">
                                        {expandedIdx === i ? '▾' : '▸'}
                                    </span>
                                </button>

                                {/* Expanded full text */}
                                {expandedIdx === i && (
                                    <div className="px-4 py-3 bg-gray-950 border-t border-gray-800">
                                        <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                                            {chunk.text}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <p className="text-xs text-gray-700 mt-3 text-right">
                        Chunks are sorted by position in the original script (chunk_index).
                        Vectors are not shown — they are 1,536-dimension float arrays.
                    </p>
                </div>
            )}
        </div>
    );
};

const SourceList = ({ sources }) => {
    const [open, setOpen] = useState(false);
    if (!sources || sources.length === 0) return null;
    return (
        <div className="mt-2">
            <button
                onClick={() => setOpen(o => !o)}
                className="text-xs text-gray-500 hover:text-gray-400 transition-colors"
            >
                {open ? '▾ Hide' : '▸ Show'} {sources.length} source excerpt{sources.length !== 1 ? 's' : ''}
            </button>
            {open && (
                <div className="mt-2 space-y-2">
                    {sources.map((s, i) => (
                        <div
                            key={i}
                            className="bg-gray-900 border border-gray-700 rounded p-3 text-xs text-gray-400 font-mono whitespace-pre-wrap"
                        >
                            <span className="text-gray-600">Excerpt {s.chunk_index + 1}: </span>
                            {s.excerpt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ChatScript = () => {
    const { userDetails } = useUserDetialsContext();

    const [step, setStep] = useState(STEP.UPLOAD);
    const [submissions, setSubmissions] = useState([]);
    const [selectedSubmissionId, setSelectedSubmissionId] = useState('');
    const [loadingSubmissions, setLoadingSubmissions] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [filename, setFilename] = useState('');
    const [totalChunks, setTotalChunks] = useState(0);
    const [chunksEmbedded, setChunksEmbedded] = useState(0);
    const [progressPct, setProgressPct] = useState(0);
    const [ingestError, setIngestError] = useState(null);

    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [chatError, setChatError] = useState(null);
    const [lastRouteUsed, setLastRouteUsed] = useState(null);

    const pollRef = useRef(null);
    const messagesEndRef = useRef(null);

    const authHeader = { Authorization: `Bearer ${userDetails?.tokenId}` };

    // Auto-scroll to latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Kick off polling once we have a jobId and are in PROCESSING step
    useEffect(() => {
        if (step !== STEP.PROCESSING || !jobId) return;

        const poll = async () => {
            try {
                const res = await fetch(`${API_BASE}/rag/v1/status/${jobId}/`, {
                    headers: authHeader,
                });
                const data = await res.json();
                setJobStatus(data.status);
                setTotalChunks(data.total_chunks || 0);
                setChunksEmbedded(data.chunks_embedded || 0);
                setProgressPct(data.progress_pct || 0);

                if (data.status === 'COMPLETED') {
                    clearInterval(pollRef.current);
                    setStep(STEP.CHAT);
                } else if (data.status === 'FAILED') {
                    clearInterval(pollRef.current);
                    setIngestError(data.error_message || 'Indexing failed');
                    setStep(STEP.UPLOAD);
                }
            } catch (e) {
                console.error('[RAG] polling error', e);
            }
        };

        pollRef.current = setInterval(poll, 3000);
        poll(); // immediate first check
        return () => clearInterval(pollRef.current);
    }, [step, jobId]);

    useEffect(() => {
        if (step !== STEP.UPLOAD) return;
        const loadSubmissions = async () => {
            setLoadingSubmissions(true);
            setIngestError(null);
            try {
                const response = await getSubmissionsDataApi(
                    1,
                    { isArchived: false, status: 'COMPLETED' },
                    '',
                    'created_at',
                    -1
                );
                const rows = response?.data?.data || [];
                const scriptRows = rows.filter(
                    (row) =>
                        row?.type === 'SCRIPT' &&
                        row?.text_id &&
                        row?.status === 'COMPLETED'
                );
                setSubmissions(scriptRows);
            } catch (e) {
                setIngestError('Failed to load your scripts. Please refresh and try again.');
            } finally {
                setLoadingSubmissions(false);
            }
        };
        loadSubmissions();
    }, [step]);

    const handleIngestFromSubmission = async () => {
        if (!selectedSubmissionId) return;
        setIngestError(null);

        try {
            const selected = submissions.find((s) => s._id === selectedSubmissionId);
            const res = await fetch(`${API_BASE}/rag/v1/ingest/from-submission/`, {
                method: 'POST',
                headers: { ...authHeader, 'Content-Type': 'application/json' },
                body: JSON.stringify({ submission_id: selectedSubmissionId }),
            });
            const data = await res.json();
            if (!res.ok) {
                setIngestError(data.error || 'Ingest failed');
                return;
            }
            setJobId(data.job_id);
            setFilename(data.filename || selected?.title || 'Submission Script');
            setTotalChunks(data.total_chunks || 0);
            setChunksEmbedded(data.chunks_embedded || 0);
            setProgressPct(data.progress_pct || 0);
            if (data.cached || data.status === 'COMPLETED') {
                setJobStatus('COMPLETED');
                setStep(STEP.CHAT);
            } else {
                setJobStatus(data.status || 'PENDING');
                setStep(STEP.PROCESSING);
            }
        } catch (e) {
            setIngestError(String(e));
        }
    };

    const handleSend = async () => {
        const q = inputValue.trim();
        if (!q || isSending) return;

        const userMsg = { role: 'user', content: q };
        const nextMessages = [...messages, userMsg];
        setMessages(nextMessages);
        setInputValue('');
        setIsSending(true);
        setChatError(null);

        // Build history array (exclude the message we just added)
        const history = messages.map(m => ({ role: m.role, content: m.content }));

        try {
            const res = await fetch(`${API_BASE}/rag/v1/chat/${jobId}/`, {
                method: 'POST',
                headers: { ...authHeader, 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: q, history }),
            });
            const data = await res.json();
            if (!res.ok) {
                setChatError(data.error || 'Chat request failed');
            } else {
                const routeUsed = data.route_used || 'rag';
                setLastRouteUsed(routeUsed);
                setMessages(prev => [
                    ...prev,
                    {
                        role: 'assistant',
                        content: data.answer,
                        sources: data.sources,
                        route_used: routeUsed,
                    },
                ]);
            }
        } catch (e) {
            setChatError(String(e));
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleReset = () => {
        clearInterval(pollRef.current);
        setStep(STEP.UPLOAD);
        setSelectedSubmissionId('');
        setJobId(null);
        setJobStatus(null);
        setFilename('');
        setTotalChunks(0);
        setChunksEmbedded(0);
        setProgressPct(0);
        setIngestError(null);
        setMessages([]);
        setInputValue('');
        setChatError(null);
        setLastRouteUsed(null);
    };

    return (
        <PageContainer>
            <div className="max-w-3xl mx-auto py-8 px-4">

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-white">Chat with Script</h1>
                    <p className="text-gray-400 text-sm mt-1">
                        Select an existing script from your material log and ask questions about it.
                    </p>
                </div>

                {/* ── UPLOAD STEP ── */}
                {step === STEP.UPLOAD && (
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-8">
                        <div className="text-center">
                            <div className="text-4xl mb-4">📚</div>
                            <h2 className="text-lg font-semibold text-white mb-2">Select an existing script</h2>
                            <p className="text-gray-500 text-sm mb-6">
                                Choose from completed scripts already uploaded to your account.
                            </p>

                            {loadingSubmissions ? (
                                <div className="flex justify-center py-8">
                                    <Spinner size="sm" />
                                </div>
                            ) : submissions.length === 0 ? (
                                <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 text-sm text-gray-400">
                                    No completed scripts found with source text. Generate coverage first, then return here.
                                </div>
                            ) : (
                                <div className="max-h-72 overflow-y-auto space-y-2 text-left">
                                    {submissions.map((submission) => {
                                        const isSelected = selectedSubmissionId === submission._id;
                                        return (
                                            <button
                                                key={submission._id}
                                                onClick={() => setSelectedSubmissionId(submission._id)}
                                                className={`w-full border rounded-lg px-4 py-3 transition-colors ${
                                                    isSelected
                                                        ? 'border-purple-500 bg-purple-900/20'
                                                        : 'border-gray-700 bg-gray-800/40 hover:bg-gray-800'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between gap-4">
                                                    <div className="min-w-0">
                                                        <p className="text-sm text-gray-200 font-medium truncate">
                                                            {submission.title || 'Untitled Script'}
                                                        </p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            {submission.type} {submission.created_at ? `• ${formatSubmissionDate(submission.created_at)}` : ''}
                                                        </p>
                                                    </div>
                                                    {isSelected && <span className="text-xs text-purple-300">Selected</span>}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {ingestError && (
                                <div className="mt-4 bg-red-950/40 border border-red-800 rounded-lg p-3 text-red-400 text-sm">
                                    {ingestError}
                                </div>
                            )}

                            <button
                                onClick={handleIngestFromSubmission}
                                disabled={!selectedSubmissionId || loadingSubmissions || submissions.length === 0}
                                className="mt-6 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white font-medium px-8 py-3 rounded-lg transition-colors text-sm"
                            >
                                Start Chat
                            </button>
                        </div>
                    </div>
                )}

                {/* ── PROCESSING STEP ── */}
                {step === STEP.PROCESSING && (
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-8">
                        <div className="flex items-center gap-4 mb-6">
                            <Spinner size="md" />
                            <div>
                                <h2 className="text-base font-semibold text-white">
                                    {jobStatus === 'PROCESSING' ? 'Embedding chunks…' : 'Queued — waiting for worker…'}
                                </h2>
                                {filename && (
                                    <p className="text-sm text-gray-500 mt-0.5">{filename}</p>
                                )}
                            </div>
                        </div>

                        {/* Progress bar — visible once total_chunks is known */}
                        {totalChunks > 0 ? (
                            <div>
                                <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                                    <span>{chunksEmbedded.toLocaleString()} / {totalChunks.toLocaleString()} chunks embedded</span>
                                    <span>{progressPct}%</span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${progressPct}%` }}
                                    />
                                </div>
                                <p className="text-xs text-gray-600 mt-3 text-center">
                                    Large documents (300–650 pages) take 2–5 minutes. The page will update automatically.
                                </p>
                            </div>
                        ) : (
                            <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                <div className="bg-purple-500/40 h-2 rounded-full animate-pulse w-full" />
                            </div>
                        )}
                    </div>
                )}

                {/* ── CHAT STEP ── */}
                {step === STEP.CHAT && (
                    <>
                    <div className="flex flex-col h-[70vh] bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">

                        {/* Chat header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700 bg-gray-900/80">
                            <div>
                                <span className="text-white font-medium text-sm">{filename}</span>
                                <span className="ml-3 text-xs text-gray-500">{totalChunks} chunks indexed</span>
                            </div>
                            <button
                                onClick={handleReset}
                                className="text-xs text-gray-500 hover:text-gray-300 border border-gray-700 hover:border-gray-500 px-3 py-1 rounded transition-colors"
                            >
                                Choose another script
                            </button>
                        </div>

                        {/* Messages area */}
                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                            {messages.length === 0 && (
                                <div className="text-center text-gray-600 text-sm mt-8">
                                    <p className="text-2xl mb-2">💬</p>
                                    <p>Ask anything about the script.</p>
                                    <p className="mt-1 text-xs">e.g. "Who is the protagonist?" · "What happens at the climax?" · "Describe the antagonist."</p>
                                </div>
                            )}

                            {messages.map((msg, i) => (
                                <div
                                    key={i}
                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                                            msg.role === 'user'
                                                ? 'bg-purple-700 text-white rounded-br-sm'
                                                : 'bg-gray-800 text-gray-200 rounded-bl-sm'
                                        }`}
                                    >
                                        <div className="whitespace-pre-wrap">{msg.content}</div>
                                        {msg.role === 'assistant' && (
                                            <>
                                                <div className="mt-2 text-[10px] uppercase tracking-wide text-gray-500">
                                                    {ROUTE_LABELS[msg.route_used] || 'RAG + synopsis'}
                                                </div>
                                                <SourceList sources={msg.sources} />
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {isSending && (
                                <div className="flex justify-start">
                                    <div className="bg-gray-800 text-gray-400 rounded-2xl rounded-bl-sm px-4 py-3 text-sm flex items-center gap-3">
                                        <Spinner size="sm" />
                                        <span>Routing + analyzing…</span>
                                    </div>
                                </div>
                            )}

                            {chatError && (
                                <div className="text-center text-red-400 text-xs bg-red-950/30 border border-red-800/50 rounded-lg px-4 py-2">
                                    {chatError}
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input bar */}
                        <div className="border-t border-gray-700 px-4 py-3 bg-gray-900/80">
                            <div className="flex items-end gap-3">
                                <textarea
                                    value={inputValue}
                                    onChange={e => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Ask about the script…"
                                    rows={1}
                                    className="flex-1 bg-gray-800 border border-gray-600 focus:border-purple-500 text-gray-200 placeholder-gray-600 rounded-xl px-4 py-3 text-sm resize-none outline-none transition-colors"
                                    style={{ minHeight: '44px', maxHeight: '140px', overflowY: 'auto' }}
                                    onInput={e => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
                                    }}
                                    disabled={isSending}
                                />
                                <button
                                    onClick={handleSend}
                                    disabled={!inputValue.trim() || isSending}
                                    className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-colors flex-shrink-0"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                                    </svg>
                                </button>
                            </div>
                            <p className="text-xs text-gray-600 mt-2 text-center">
                                Enter to send · Shift+Enter for new line · Answers use routing (RAG + synopsis or full script)
                            </p>
                        </div>
                    </div>

                    {/* Vector Index Inspector — collapsible, below the chat box */}
                    <VectorInspector jobId={jobId} authHeader={authHeader} />
                    </>
                )}
            </div>
        </PageContainer>
    );
};

export default ChatScript;
